import React from 'react';
import { Car } from 'lucide-react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { services, serviceAddons } from '../../utils/mock';

const ServiceSelectionSection = ({ formData, handleSelectChange, handleAddonChange }) => {
  return (
    <>
      {/* Service Selection */}
      <div className="space-y-2">
        <Label htmlFor="service" className="text-white flex items-center space-x-2">
          <Car className="w-4 h-4 text-red-500" />
          <span>Service Package *</span>
        </Label>
        <Select
          value={formData.service}
          onValueChange={(value) => handleSelectChange('service', value)}
          required
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="Select service package" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {services.map((service) => {
              const price = formData.vehicleType && service.pricing[formData.vehicleType] 
                ? service.pricing[formData.vehicleType] 
                : null;
              const isUnavailable = service.unavailable === true;
              return (
                <SelectItem
                  key={service.id}
                  value={service.title}
                  disabled={isUnavailable}
                  className={`${isUnavailable ? 'opacity-50 cursor-not-allowed' : 'text-white hover:bg-zinc-700'}`}
                >
                  <div className="flex justify-between items-center w-full gap-2">
                    <span className={isUnavailable ? 'text-gray-500' : ''}>{service.title}</span>
                    {isUnavailable && (
                      <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded border border-orange-500/30">
                        COMING SOON
                      </span>
                    )}
                    {!isUnavailable && price && <span className="ml-4 text-gray-400">${price}</span>}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Add-on Selection */}
      <div className="space-y-2">
        <Label className="text-white flex items-center space-x-2">
          <Car className="w-4 h-4 text-red-500" />
          <span>Add-on Services (Optional - Select Multiple)</span>
        </Label>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-3">
          {serviceAddons.map((addon) => {
            const addonPrice = formData.vehicleType && addon.pricing[formData.vehicleType] 
              ? addon.pricing[formData.vehicleType] 
              : null;
            const isUnavailable = addon.unavailable === true;
            const displayPrice = addon.priceRange || (addonPrice ? `$${addonPrice}` : null);
            return (
              <div key={addon.id} className={`flex items-start space-x-3 ${isUnavailable ? 'opacity-50' : ''}`}>
                <Checkbox
                  id={`addon-${addon.id}`}
                  checked={formData.addons.includes(addon.title)}
                  onCheckedChange={() => !isUnavailable && handleAddonChange(addon.title)}
                  disabled={isUnavailable}
                  className="mt-1"
                />
                <label
                  htmlFor={`addon-${addon.id}`}
                  className={`flex-1 ${isUnavailable ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex justify-between items-center gap-2">
                    <span className={`font-medium ${isUnavailable ? 'text-gray-500' : 'text-white'}`}>
                      {addon.title}
                    </span>
                    <div className="flex items-center gap-2">
                      {isUnavailable && (
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded border border-orange-500/30 whitespace-nowrap">
                          COMING SOON
                        </span>
                      )}
                      {!isUnavailable && displayPrice && (
                        <span className="text-red-500 font-semibold whitespace-nowrap">{displayPrice}</span>
                      )}
                    </div>
                  </div>
                  <p className={`text-sm mt-1 ${isUnavailable ? 'text-gray-500' : 'text-gray-400'}`}>
                    {addon.description}
                  </p>
                </label>
              </div>
            );
          })}
        </div>
        
        {/* Pricing Disclaimer for Condition-Based Services */}
        <div className="mt-3 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <p className="text-blue-300 text-sm">
            <span className="font-semibold">💡 Note:</span> Final pricing for services like shampooing, odor elimination, and pet hair removal may vary based on vehicle condition and severity. We'll always confirm the final price before starting work.
          </p>
        </div>
      </div>
    </>
  );
};

export default ServiceSelectionSection;
